import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, PenLine, RotateCcw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { company } from "@/lib/branding";

/** Public agreement signing page — no login required, unique-token gated.
 *  Mobile-friendly: signature captured on a canvas via pointer events so
 *  finger, stylus, and mouse all work. */
export default function AgreementSignPage() {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState("");
    const [consent, setConsent] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const canvasRef = useRef(null);
    const drawing = useRef(false);
    const hasStrokes = useRef(false);
    const API = process.env.REACT_APP_BACKEND_URL;

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/api/public/agreements/sign/${token}`);
                setInfo(data);
                if (data.status === "signed") setDone(true);
                if (data.customer?.name) setName(data.customer.name);
            } catch (e) {
                toast.error("This signing link is invalid or expired");
            } finally { setLoading(false); }
        })();
    }, [API, token]);

    // Canvas setup — high-DPI aware
    useEffect(() => {
        const c = canvasRef.current;
        if (!c) return;
        const ratio = window.devicePixelRatio || 1;
        const rect = c.getBoundingClientRect();
        c.width = rect.width * ratio;
        c.height = rect.height * ratio;
        const ctx = c.getContext("2d");
        ctx.scale(ratio, ratio);
        ctx.lineWidth = 2.4;
        ctx.lineCap = "round";
        ctx.strokeStyle = "#111";
    }, [info]);

    const relPoint = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
        const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
        return { x, y };
    };

    const startDraw = (e) => {
        e.preventDefault();
        drawing.current = true;
        const { x, y } = relPoint(e);
        const ctx = canvasRef.current.getContext("2d");
        ctx.beginPath();
        ctx.moveTo(x, y);
    };
    const draw = (e) => {
        if (!drawing.current) return;
        e.preventDefault();
        const { x, y } = relPoint(e);
        const ctx = canvasRef.current.getContext("2d");
        ctx.lineTo(x, y);
        ctx.stroke();
        hasStrokes.current = true;
    };
    const endDraw = () => { drawing.current = false; };

    const clearPad = () => {
        const c = canvasRef.current;
        c.getContext("2d").clearRect(0, 0, c.width, c.height);
        hasStrokes.current = false;
    };

    const submit = async () => {
        if (!hasStrokes.current) return toast.error("Please draw your signature");
        if (!name.trim()) return toast.error("Please type your full legal name");
        if (!consent) return toast.error("Please check the consent box");
        setSubmitting(true);
        try {
            const dataUrl = canvasRef.current.toDataURL("image/png");
            await axios.post(`${API}/api/public/agreements/sign/${token}`, {
                signed_name: name.trim(),
                signature_png_data_url: dataUrl,
            });
            setDone(true);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Signature failed — try again");
        } finally { setSubmitting(false); }
    };

    if (loading) return <div className="p-8 text-center text-muted-foreground">Loading…</div>;
    if (!info) return <div className="p-8 text-center text-red-600">Invalid or expired signing link.</div>;

    if (done) {
        return (
            <div className="max-w-lg mx-auto p-6 text-center" data-testid="agreement-sign-done">
                <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
                <h1 className="text-2xl font-bold mb-2">Thanks — you're all set!</h1>
                <p className="text-muted-foreground">
                    Your signed agreement is on file. A copy is on its way to your email.
                </p>
                <div className="mt-6 text-xs text-muted-foreground">
                    Signed on {info.signed_at ? new Date(info.signed_at).toLocaleString() : "just now"}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background p-4 sm:p-6" data-testid="agreement-sign-page">
            <div className="max-w-3xl mx-auto space-y-4">
                <div className="text-center">
                    <ShieldCheck className="w-10 h-10 mx-auto text-primary mb-2" />
                    <h1 className="text-2xl font-bold">{company()} Service Agreement</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Please review the agreement below, then sign at the bottom.
                    </p>
                </div>

                <div className="rounded-md border border-border overflow-hidden">
                    <iframe
                        src={`${API}/api/public/agreements/pdf/${token}`}
                        title="Customer Service Agreement"
                        className="w-full"
                        style={{ height: "min(70vh, 700px)" }}
                        data-testid="agreement-pdf-iframe"
                    />
                </div>

                <div className="rounded-md border border-border p-4 space-y-4 bg-card">
                    <div>
                        <Label className="text-xs mb-1 block">Type your full legal name</Label>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Jane Q. Public"
                            data-testid="agreement-name-input"
                        />
                    </div>

                    <div>
                        <Label className="text-xs mb-1 block flex items-center gap-2">
                            <PenLine className="w-3.5 h-3.5" />
                            Draw your signature below
                        </Label>
                        <div className="relative rounded-md border border-dashed border-input bg-background"
                             style={{ height: 160 }}>
                            <canvas
                                ref={canvasRef}
                                className="w-full h-full touch-none cursor-crosshair"
                                onMouseDown={startDraw}
                                onMouseMove={draw}
                                onMouseUp={endDraw}
                                onMouseLeave={endDraw}
                                onTouchStart={startDraw}
                                onTouchMove={draw}
                                onTouchEnd={endDraw}
                                data-testid="agreement-signature-canvas"
                            />
                            <Button
                                variant="ghost"
                                size="sm"
                                className="absolute top-1 right-1 text-xs"
                                onClick={clearPad}
                                data-testid="agreement-signature-clear"
                            >
                                <RotateCcw className="w-3 h-3 mr-1" />
                                Clear
                            </Button>
                        </div>
                    </div>

                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                        <Checkbox
                            checked={consent}
                            onCheckedChange={setConsent}
                            data-testid="agreement-consent-checkbox"
                            className="mt-0.5"
                        />
                        <span>
                            I have reviewed and agree to be bound by the terms of the {company()}
                            Customer Service Agreement above. I understand my signature is legally
                            binding under the federal E-SIGN Act.
                        </span>
                    </label>

                    <Button
                        className="w-full"
                        onClick={submit}
                        disabled={submitting || !consent}
                        data-testid="agreement-submit-btn"
                    >
                        {submitting ? "Signing…" : "Sign & finish"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
